'use strict';

/**
 * analyticsHelper.js — Client-side analytics / PII hashing utilities.
 *
 * Single source of truth for all browser-side SHA-256 hashing, PII
 * normalisation, userData assembly, and session-tracking helpers.
 *
 * Consumed by:
 *   - int_gtm/cartridge/client/default/js/datalayer.js
 *   - app_honda_cars/cartridge/client/default/js/product/configurator.js
 *   - any future Meta / TikTok / GA4 client-side integration
 *
 * Normalisation rules are identical to the server-side counterpart at:
 *   app_honda_cars/cartridge/scripts/helpers/analyticsHelper.js
 * so hashes produced on both sides always match.
 */

/** Australian mobile country code (without leading +). */
var AU_COUNTRY_CODE = '61';

/**
 * In-memory named timers for `timeStart` / `timeLap` / `timeEnd`.
 *
 * @type {Object<string, { startedAt: number, markedAt: number }>}
 */
const NAMED_TIMERS = {};

/**
 * SHA-256 hash a string using the browser SubtleCrypto API.
 * Returns a Promise resolving to a 64-char lowercase hex string.
 * Resolves to null if the input is empty or SubtleCrypto is unavailable.
 * @param {string} str
 * @returns {Promise<string|null>}
 */
function sha256(str) {
    if (!str || !window.crypto || !window.crypto.subtle) {
        return Promise.resolve(null);
    }
    var buf = new TextEncoder().encode(str);
    return window.crypto.subtle.digest('SHA-256', buf).then(function (hashBuf) {
        return Array.from(new Uint8Array(hashBuf))
            .map(function (b) { return b.toString(16).padStart(2, '0'); })
            .join('');
    }).catch(function () { return null; });
}

/**
 * Normalise an email for Google Enhanced Conversions:
 * lowercase, trim; for Gmail addresses remove dots and +suffix from local part.
 * @param {string} email
 * @returns {string|null}
 */
function normaliseEmailGoogle(email) {
    if (!email) return null;
    email = email.toLowerCase().trim();
    var parts = email.split('@');
    if (parts.length !== 2) return email;
    var local = parts[0];
    var domain = parts[1];
    if (domain === 'gmail.com' || domain === 'googlemail.com') {
        local = local.split('+')[0].replace(/\./g, '');
    }
    return local + '@' + domain;
}

/**
 * Normalise an email for Meta CAPI / TikTok:
 * lowercase and trim only — keep dots, do not strip +suffix.
 * @param {string} email
 * @returns {string|null}
 */
function normaliseEmailMeta(email) {
    if (!email) return null;
    return email.toLowerCase().trim();
}

/**
 * Normalise a phone number for Google Enhanced Conversions / TikTok.
 * Strips whitespace/dashes/parens; converts AU local format (0xxx) to E.164
 * (+61xxx); any other number without a leading + gets one added.
 * @param {string} phone
 * @returns {string|null}
 */
function normalisePhoneGoogleTT(phone) {
    if (!phone) return null;
    phone = phone.replace(/[\s\-()]/g, '');
    if (/^0\d/.test(phone)) {
        // AU local: 04xx... → +614xx...
        phone = '+' + AU_COUNTRY_CODE + phone.slice(1);
    } else if (phone.charAt(0) !== '+') {
        phone = '+' + phone;
    }
    return phone;
}

/**
 * Normalise a phone number for Meta CAPI:
 * same as Google/TikTok but WITHOUT the leading + prefix.
 * @param {string} phone
 * @returns {string|null}
 */
function normalisePhoneMeta(phone) {
    if (!phone) return null;
    var normalised = normalisePhoneGoogleTT(phone);
    return normalised ? normalised.replace(/^\+/, '') : null;
}

/**
 * Normalise a name field (first or last):
 * lowercase, trim, and strip common English honorific prefixes.
 * @param {string} name
 * @returns {string|null}
 */
function normaliseName(name) {
    if (!name) return null;
    return name.toLowerCase().trim().replace(/^(mr|mrs|ms|dr|prof)\.?\s+/i, '');
}

/**
 * Normalise then SHA-256 hash an email for Google Enhanced Conversions.
 * @param {string} email
 * @returns {Promise<string|null>}
 */
function hashEmailGoogle(email) {
    return sha256(normaliseEmailGoogle(email));
}

/**
 * Normalise then SHA-256 hash an email for Meta CAPI / TikTok.
 * @param {string} email
 * @returns {Promise<string|null>}
 */
function hashEmailMeta(email) {
    return sha256(normaliseEmailMeta(email));
}

/**
 * Normalise then SHA-256 hash a phone number for Google / TikTok (E.164 with +).
 * @param {string} phone
 * @returns {Promise<string|null>}
 */
function hashPhoneGoogleTT(phone) {
    return sha256(normalisePhoneGoogleTT(phone));
}

/**
 * Normalise then SHA-256 hash a phone number for Meta CAPI (E.164 without +).
 * @param {string} phone
 * @returns {Promise<string|null>}
 */
function hashPhoneMeta(phone) {
    return sha256(normalisePhoneMeta(phone));
}

/**
 * Normalise then SHA-256 hash a name field (all platforms).
 * @param {string} name
 * @returns {Promise<string|null>}
 */
function hashName(name) {
    return sha256(normaliseName(name));
}

/**
 * Hash all PII fields and return a unified userData object compatible with
 * GTM dataLayer, Google Enhanced Conversions, Meta CAPI and TikTok.
 *
 * Unavailable / empty fields resolve to null (never empty string).
 *
 * @param {{ email?: string, phone?: string, firstName?: string, lastName?: string }} rawData
 * @returns {Promise<{
 *   email_address: string|null,   // Google Enhanced Conversions
 *   em:            string|null,   // Meta CAPI + TikTok
 *   phone_number:  string|null,   // Google EC + TikTok  (E.164 with +)
 *   ph:            string|null,   // Meta CAPI           (E.164 without +)
 *   first_name:    string|null,   // All platforms
 *   last_name:     string|null    // All platforms
 * }|null>}
 */
function hashUserData(rawData) {
    if (!rawData) return Promise.resolve(null);
    return Promise.all([
        hashEmailGoogle(rawData.email),
        hashEmailMeta(rawData.email),
        hashPhoneGoogleTT(rawData.phone),
        hashPhoneMeta(rawData.phone),
        hashName(rawData.firstName),
        hashName(rawData.lastName)
    ]).then(function (results) {
        return {
            email_address: results[0] || null,
            em: results[1] || null,
            phone_number: results[2] || null,
            ph: results[3] || null,
            first_name: results[4] || null,
            last_name: results[5] || null
        };
    });
}

/**
 * Extract user-entered PII from a jQuery form element.
 * Returns raw (unhashed) strings — call hashUserData() on the result.
 * @param {jQuery} $form
 * @returns {{ email: string, phone: string, firstName: string, lastName: string }}
 */
function extractFormPII($form) {
    return {
        email: ($form.find('[name*="email"],[id*="email"]').first().val() || '').trim(),
        phone: ($form.find('[name*="phone"],[name*="mobile"], [id*="phone"]').first().val() || '').trim(),
        firstName: ($form.find('[name*="firstName"], [name*="firstname"], [id*="first"]').first().val() || '').trim(),
        lastName: ($form.find('[name*="lastName"],  [name*="lastname"],  [id*="last"]').first().val() || '').trim()
    };
}

/**
 * Read a browser cookie by name. Returns empty string if not found.
 * @param {string} cookieName
 * @returns {string}
 */
function getCookie(cookieName) {
    var name = cookieName + '=';
    var decodedCookie = decodeURIComponent(document.cookie);
    var cookieArray = decodedCookie.split(';');
    for (var i = 0; i < cookieArray.length; i++) {
        var cookieItem = cookieArray[i];
        while (cookieItem.charAt(0) === ' ') {
            cookieItem = cookieItem.substring(1);
        }
        if (cookieItem.indexOf(name) === 0) {
            return cookieItem.substring(name.length, cookieItem.length);
        }
    }
    return '';
}

/**
 * Finds the first cookie whose name starts with the given prefix.
 * Returns an object { name, value } or null if not found.
 * Use this for dynamic cookie names like _ga_XXXXXX or dwanonymous_*.
 * @param {string} prefix
 * @returns {{ name: string, value: string }|null}
 */
function getCookieByPrefix(prefix) {
    var decodedCookie = decodeURIComponent(document.cookie);
    var cookieArray = decodedCookie.split(';');
    for (var i = 0; i < cookieArray.length; i++) {
        var cookieItem = cookieArray[i].trim();
        var eqIndex = cookieItem.indexOf('=');
        if (eqIndex === -1) continue;
        var cookieName = cookieItem.substring(0, eqIndex).trim();
        if (cookieName.indexOf(prefix) === 0) {
            return { name: cookieName, value: cookieItem.substring(eqIndex + 1) };
        }
    }
    return null;
}

/**
 * Reads the GA client ID from the _ga cookie.
 * Cookie format: GA1.1.<clientIdPart1>.<clientIdPart2>
 * @returns {string|null}
 */
function getGAClientId() {
    var val = getCookie('_ga');
    if (val) {
        var parts = val.split('.');
        if (parts.length >= 4) {
            return parts[2] + '.' + parts[3];
        }
    }
    var sessionCookie = getCookieByPrefix('_ga_');
    if (sessionCookie && sessionCookie.value) {
        var sessionParts = sessionCookie.value.split('.');
        if (sessionParts.length >= 4) {
            return sessionParts[2] + '.' + sessionParts[3];
        }
    }
    return null;
}

/**
 * Reads the GA4 Measurement ID suffix from the _ga_XXXXXX cookie name.
 * e.g. cookie "_ga_ABC123" returns "ABC123".
 * @returns {string|null}
 */
function getGATrackId() {
    var cookie = getCookieByPrefix('_ga_');
    return cookie && cookie.name.length > 4 ? cookie.name.substring(4) : null;
}

/**
 * Reads the GA4 User ID from window.dataLayer.
 * Single source of truth per Google GTM/GA4 docs — user_id pushed into dataLayer.
 * Scans in reverse so the most recent push wins.
 * @returns {string|null}
 */
function getGAUserId() {
    if (window.dataLayer && Array.isArray(window.dataLayer)) {
        var fromDataLayer = window.dataLayer
            .filter(function (entry) { return entry && entry.user_id; })
            .pop();
        if (fromDataLayer) {
            return String(fromDataLayer.user_id);
        }
    }
    var uuid = getCookie('uuid');
    return uuid || null;
}

/**
 * Reads GUID from the uuid cookie, falling back to the _ga_XXXXXX cookie value.
 * @returns {string|null}
 */
function getGAGUID() {
    var uuid = getCookie('uuid');
    if (uuid) return uuid;
    var gaCookie = getCookieByPrefix('_ga_');
    if (gaCookie) {
        var parts = gaCookie.value.split('.');
        return parts.length >= 3 ? parts[2] || null : null;
    }
    return null;
}

var PII_FIELD_NAMES = ['email', 'phone', 'firstname', 'lastname', 'first_name', 'last_name', 'first-name', 'last-name', 'mobile', 'telephone'];

/**
 * For PII fields return the field *name* (type hint); for safe fields return the actual value.
 * Prevents raw PII values leaking into non-hashed dataLayer properties.
 * @param {string} fieldName
 * @param {string} fieldValue
 * @returns {string}
 */
function getPIISafeValue(fieldName, fieldValue) {
    var name = (fieldName || '').toLowerCase();
    var isPII = PII_FIELD_NAMES.some(function (pii) { return name.indexOf(pii) !== -1; });
    return isPII ? fieldName : (fieldValue || '');
}

/**
 * Returns true (and sets a flag) only on the FIRST call per formId per session.
 * Subsequent calls return false — ensures a 'start' event fires only once.
 * @param {string} formId  unique identifier for the form journey
 * @returns {boolean}
 */
function isFirstFormInteraction(formId) {
    var key = 'formStarted_' + formId;
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
    return true;
}

/**
 * Fire `callback` once when `selector` element scrolls into view (20% threshold).
 * Uses IntersectionObserver; falls back to immediate fire if unavailable.
 * @param {string}   selector  CSS selector for the target element
 * @param {Function} callback  called once with the observed element
 */
function onFormVisible(selector, callback) {
    var target = document.querySelector(selector);
    if (!target) return;
    if (!('IntersectionObserver' in window)) {
        callback(target);
        return;
    }
    var observer = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
            if (entry.isIntersecting) {
                callback(entry.target);
                obs.unobserve(entry.target);
            }
        });
    }, { threshold: 0.2 });
    observer.observe(target);
}

/**
 * Initialise page-level session tracking.
 * - Sets sessionStartTime in sessionStorage on first call (per session).
 * - Increments and returns the running pageViewCount.
 * Call once on every page load inside document.ready.
 * @returns {number} current page view count for this session
 */
function initPageTracking() {
    if (!sessionStorage.getItem('sessionStartTime')) {
        sessionStorage.setItem('sessionStartTime', String(Date.now()));
    }
    var count = parseInt(sessionStorage.getItem('pageViewCount') || '0', 10) + 1;
    sessionStorage.setItem('pageViewCount', String(count));
    return count;
}

/**
 * Return a human-readable time-spent bucket based on elapsed session time.
 * Optionally scoped to a named sub-timer stored in sessionStorage
 * (e.g. a per-form start time).
 *
 * @param {string} [timerKey='sessionStartTime'] sessionStorage key to measure from
 *
 * @returns {string}
 */
function getTimeSpent(timerKey) {
    var key = timerKey || 'sessionStartTime';
    var start = parseInt(sessionStorage.getItem(key) || String(Date.now()), 10);
    var secs = (Date.now() - start) / 1000;

    return formatTimeBucket(secs);
}

/**
 * Map seconds onto the GTM `timeSpent` buckets.
 *
 * Buckets: '0-30 sec' | '30-60 sec' | '1-2 min'   | '2-5 min' |
 *          '5-10 min' | '10-20 min' | '20-30 min' | '30+ min'
 *
 * @param {number} secs
 *
 * @returns {string}
 */
function formatTimeBucket(secs) {
    if (!(secs >= 30)) return '0-30 sec';
    if (secs < 60) return '30-60 sec';
    if (secs < 120) return '1-2 min';
    if (secs < 300) return '2-5 min';
    if (secs < 600) return '5-10 min';
    if (secs < 1200) return '10-20 min';
    if (secs < 1800) return '20-30 min';

    return '30+ min';
}

/**
 * Start or restart a named in-memory timer. No-op when `key` is empty.
 *
 * @param {string} key
 */
function timeStart(key) {
    if (!key) {
        return;
    }

    const now = Date.now();

    NAMED_TIMERS[key] = { startedAt: now, markedAt: now };
}

/**
 * Elapsed since the last start/lap for `key`, then move the lap mark to now.
 * Empty key or never started — does not create a timer.
 *
 * @param {string} key
 * @param {boolean} [format=true] `true` → GTM bucket (seconds). `false` → milliseconds.
 *
 * @returns {string|number}
 */
function timeLap(key, format = true) {
    if (!key) {
        return format ? formatTimeBucket(0) : 0;
    }

    const timer = NAMED_TIMERS[key];

    if (!timer) {
        return format ? formatTimeBucket(0) : 0;
    }

    const now = Date.now();
    const elapsed = now - timer.markedAt;

    timer.markedAt = now;

    return format ? formatTimeBucket(elapsed / 1000) : elapsed;
}

/**
 * Elapsed since `timeStart` for `key`, then delete the timer.
 *
 * @param {string} key
 * @param {boolean} [format=true] `true` → GTM bucket (seconds). `false` → milliseconds.
 *
 * @returns {string|number}
 */
function timeEnd(key, format = true) {
    if (!key) {
        return format ? formatTimeBucket(0) : 0;
    }

    const timer = NAMED_TIMERS[key];

    if (!timer) {
        return format ? formatTimeBucket(0) : 0;
    }

    const elapsed = Date.now() - timer.startedAt;

    delete NAMED_TIMERS[key];

    return format ? formatTimeBucket(elapsed / 1000) : elapsed;
}

module.exports = {
    sha256: sha256,
    normaliseEmailGoogle: normaliseEmailGoogle,
    normaliseEmailMeta: normaliseEmailMeta,
    normalisePhoneGoogleTT: normalisePhoneGoogleTT,
    normalisePhoneMeta: normalisePhoneMeta,
    normaliseName: normaliseName,
    hashEmailGoogle: hashEmailGoogle,
    hashEmailMeta: hashEmailMeta,
    hashPhoneGoogleTT: hashPhoneGoogleTT,
    hashPhoneMeta: hashPhoneMeta,
    hashName: hashName,
    hashUserData: hashUserData,
    extractFormPII: extractFormPII,
    initPageTracking: initPageTracking,
    getTimeSpent: getTimeSpent,
    formatTimeBucket: formatTimeBucket,
    isFirstFormInteraction: isFirstFormInteraction,
    onFormVisible: onFormVisible,
    getPIISafeValue: getPIISafeValue,
    getCookie: getCookie,
    getCookieByPrefix: getCookieByPrefix,
    getGAClientId: getGAClientId,
    getGATrackId: getGATrackId,
    getGAUserId: getGAUserId,
    getGAGUID: getGAGUID,
    timeStart: timeStart,
    timeLap: timeLap,
    timeEnd: timeEnd,
};
